import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { ImportDialog } from '../../components/ui/ImportDialog'

interface CostCenter {
  id: number
  code: string
  name: string
  department: string
  defaultAccount: string
  active: boolean
}

const Schema = z.object({
  code: z.string().regex(/^[A-Z]{2,5}-\d{3,4}$/, 'Format: ABC-001 sau ABCD-0001'),
  name: z.string().min(1, 'Denumire obligatorie'),
  department: z.string().min(1, 'Departament obligatoriu'),
  defaultAccount: z.string().regex(/^\d{3,8}$/, 'Cont: 3-8 cifre'),
  active: z.boolean().default(true),
})

type Input = z.infer<typeof Schema>

export function SetariCentreCostPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('settings.cost_centers:write')
  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<CostCenter | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  const [deptFilter, setDeptFilter] = useState('')

  const query = useQuery<{ items: CostCenter[] }>({
    queryKey: ['cost-centers'],
    queryFn: async () => (await api.get<{ items: CostCenter[] }>('/settings/cost-centers')).data,
  })

  const createMutation = useMutation({
    mutationFn: async (data: Input) => (await api.post('/settings/cost-centers', data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-centers'] })
      toast.success('Centru de cost adăugat')
      setCreateOpen(false)
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare la adăugare'
      toast.error(message)
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Input }) =>
      (await api.patch(`/settings/cost-centers/${id}`, data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-centers'] })
      toast.success('Actualizat')
      setEditTarget(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/settings/cost-centers/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-centers'] })
      toast.success('Șters')
    },
  })

  const items = useMemo(() => query.data?.items ?? [], [query.data])
  const departments = useMemo(
    () => Array.from(new Set(items.map((c) => c.department))).sort(),
    [items],
  )
  const filtered = useMemo(
    () => (deptFilter ? items.filter((c) => c.department === deptFilter) : items),
    [items, deptFilter],
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Centre de cost</h1>
        {canWrite && (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setImportOpen(true)}>
              Import CSV / XLSX
            </Button>
            <Button onClick={() => setCreateOpen(true)}>+ Adaugă centru de cost</Button>
          </div>
        )}
      </div>

      <Card>
        <div className="mb-3 flex items-center gap-2">
          <select
            value={deptFilter}
            onChange={(e) => setDeptFilter(e.target.value)}
            className="rounded border border-b4l-border bg-white px-3 py-1.5 text-sm"
          >
            <option value="">Toate departamentele</option>
            {departments.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          <span className="ml-auto text-xs text-b4l-text-muted">
            {filtered.length} / {items.length} centre
          </span>
        </div>

        {query.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă…</p>
        ) : items.length === 0 ? (
          <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
            Niciun centru de cost.{' '}
            {canWrite &&
              'Importă CSV cu coloanele cod, denumire, departament, cont sau adaugă manual.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                <th className="py-2 pr-3 font-medium">Cod</th>
                <th className="py-2 pr-3 font-medium">Denumire</th>
                <th className="py-2 pr-3 font-medium">Departament</th>
                <th className="py-2 pr-3 font-medium">Cont contabil</th>
                {canWrite && <th className="py-2 pr-3 font-medium">Acțiuni</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-b4l-border last:border-0">
                  <td className="py-2 pr-3 font-mono text-xs">{c.code}</td>
                  <td className="py-2 pr-3">{c.name}</td>
                  <td className="py-2 pr-3">
                    <span className="rounded bg-b4l-bg px-2 py-0.5 text-[10px]">
                      {c.department}
                    </span>
                  </td>
                  <td className="py-2 pr-3 font-mono text-xs">{c.defaultAccount}</td>
                  {canWrite && (
                    <td className="py-2 pr-3">
                      <div className="flex gap-1">
                        <Button
                          variant="secondary"
                          onClick={() => setEditTarget(c)}
                          className="!px-2 !py-1 !text-xs"
                        >
                          Editează
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => {
                            if (confirm(`Ștergi ${c.code}?`)) deleteMutation.mutate(c.id)
                          }}
                          className="!px-2 !py-1 !text-xs"
                        >
                          Șterge
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {createOpen && (
        <CostCenterFormModal
          title="Adaugă centru de cost"
          submitLabel="Creează"
          onClose={() => setCreateOpen(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          submitting={createMutation.isPending}
        />
      )}

      {editTarget && (
        <CostCenterFormModal
          title={`Editează ${editTarget.code}`}
          submitLabel="Salvează"
          initial={{
            code: editTarget.code,
            name: editTarget.name,
            department: editTarget.department,
            defaultAccount: editTarget.defaultAccount,
            active: editTarget.active,
          }}
          onClose={() => setEditTarget(null)}
          onSubmit={(data) => updateMutation.mutate({ id: editTarget.id, data })}
          submitting={updateMutation.isPending}
        />
      )}

      <ImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        endpoint="/settings/cost-centers/import"
        title="Import centre de cost"
        hint="Coloane: cod, denumire, departament, cont. Format cod: ex MKT-001. Cont: 3-8 cifre."
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['cost-centers'] })}
      />
    </div>
  )
}

interface CostCenterFormModalProps {
  title: string
  submitLabel: string
  initial?: Input
  onClose: () => void
  onSubmit: (data: Input) => void
  submitting: boolean
}

function CostCenterFormModal({
  title,
  submitLabel,
  initial,
  onClose,
  onSubmit,
  submitting,
}: CostCenterFormModalProps) {
  const form = useForm<Input>({
    resolver: zodResolver(Schema),
    defaultValues: initial ?? {
      code: '',
      name: '',
      department: 'Marketing',
      defaultAccount: '',
      active: true,
    },
  })

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={() => form.handleSubmit(onSubmit)()} loading={submitting}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <form className="space-y-3" noValidate onSubmit={(e) => e.preventDefault()}>
        <Field
          label="Cod"
          hint="Format: ABC-001 sau ABCD-0001"
          {...form.register('code')}
          error={form.formState.errors.code?.message}
          disabled={!!initial}
        />
        <Field
          label="Denumire"
          {...form.register('name')}
          error={form.formState.errors.name?.message}
        />
        <Field
          label="Departament"
          {...form.register('department')}
          error={form.formState.errors.department?.message}
        />
        <Field
          label="Cont contabil"
          hint="Plan conturi RO (ex: 6232, 621, 624)"
          {...form.register('defaultAccount')}
          error={form.formState.errors.defaultAccount?.message}
        />
      </form>
    </Modal>
  )
}
